/**
 * RFQ Types admin section — CR-5466.
 *
 * Admin-managed dropdown values surfaced on the Create RFQ wizard's
 * Basic Details step. Mirrors the CommodityGroupsSection pattern
 * (per-company, enable/disable toggle, name-unique). Extracted into its
 * own file so PlatformTab.tsx doesn't grow further beyond 1000 lines.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Loader2, Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { rfqAPI, fetchAllPages } from '../../../lib/api';
import type { RFQType } from '../../../lib/api';

export function RFQTypesSection() {
  const [types, setTypes] = useState<RFQType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RFQType | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', enabled: true });
  const [deleteTarget, setDeleteTarget] = useState<RFQType | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const list = await fetchAllPages<RFQType>((page) => rfqAPI.getRFQTypes({ page_size: 100, page }));
      setTypes(list);
    } catch (err) {
      console.error('Failed to load RFQ types', err);
      toast.error('Failed to load RFQ types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDialog = (rt?: RFQType) => {
    if (rt) {
      setEditing(rt);
      setForm({ name: rt.name, enabled: rt.enabled });
    } else {
      setEditing(null);
      setForm({ name: '', enabled: true });
    }
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await rfqAPI.updateRFQType(editing.id, form);
        toast.success('RFQ Type updated');
      } else {
        await rfqAPI.createRFQType(form);
        toast.success('RFQ Type created');
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to save RFQ type', err);
      // Surface the backend's validation message (duplicate name etc.)
      const msg = err instanceof Error
        ? (err as { response?: { data?: { name?: string[]; detail?: string } } })
            .response?.data?.name?.[0]
          ?? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          ?? err.message
        : 'Failed to save RFQ Type';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await rfqAPI.deleteRFQType(deleteTarget.id);
      toast.success('RFQ Type deleted');
      setDeleteTarget(null);
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to delete RFQ type', err);
      // PROTECT on FK — deleting a type that's referenced by an RFQ
      // returns 400/409 with a useful message. Surface it.
      toast.error('Cannot delete — this RFQ Type is referenced by one or more RFQs. Disable it instead.');
    }
  };

  const toggleEnabled = async (rt: RFQType) => {
    // Optimistic flip so the switch feels responsive.
    setTypes((prev) => prev.map((t) => (t.id === rt.id ? { ...t, enabled: !t.enabled } : t)));
    try {
      await rfqAPI.updateRFQType(rt.id, { enabled: !rt.enabled });
    } catch (err) {
      console.error('Failed to toggle', err);
      toast.error('Failed to update RFQ Type');
      await fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Manage RFQ Types
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Admin-managed dropdown values that classify each RFQ on the Create RFQ wizard.
              </p>
            </div>
            <Button size="sm" onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-1" />
              Add RFQ Type
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {types.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No RFQ Types yet</p>
              <p className="text-sm mt-1">Create one to populate the Create RFQ dropdown.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32 text-center">Status</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch checked={t.enabled} onCheckedChange={() => toggleEnabled(t)} />
                        <span className="text-xs text-muted-foreground">
                          {t.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDialog(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(t)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit RFQ Type' : 'New RFQ Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="rfqtype-name">Name</Label>
              <Input
                id="rfqtype-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Type A, Pre-qualified, Open Tender"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="rfqtype-enabled"
                checked={form.enabled}
                onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
              />
              <Label htmlFor="rfqtype-enabled">Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete RFQ Type?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Delete &ldquo;{deleteTarget?.name}&rdquo;? This cannot be undone. If any existing RFQ references it, the
            delete will be rejected — disable it instead.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
